import { createTheme } from '@mui/material/styles';

// Qatar Airways Maroon Theme - White background with maroon accents
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#8B1538', // Qatar Airways maroon - primary brand color
      light: '#A0284A', // Lighter maroon for light variant
      dark: '#6B0F2A', // Darker maroon for dark variant
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F5F5F5', // Light gray for secondary elements
      light: '#FAFAFA', // Very light gray
      dark: '#E0E0E0', // Medium gray
      contrastText: '#8B1538', // Maroon for contrast
    },
    error: {
      main: '#F44336', // Material red
      light: '#E57373',
      dark: '#D32F2F',
    },
    warning: {
      main: '#FF9800', // Orange
      light: '#FFB74D',
      dark: '#F57C00',
    },
    info: {
      main: '#8B1538', // Qatar maroon for info
      light: '#A0284A', // Lighter maroon
      dark: '#6B0F2A', // Darker maroon
    },
    success: {
      main: '#4CAF50', // Green
      light: '#81C784',
      dark: '#388E3C',
    },
    background: {
      default: '#FFFFFF', // Pure white background
      paper: '#FFFFFF', // White for cards/grid
      sidebar: '#FFFFFF', // White for sidebar
    },
    text: {
      primary: '#333333', // Dark gray for primary text
      secondary: '#666666', // Medium gray for secondary text
      disabled: '#CCCCCC', // Light gray for disabled text
    },
    divider: 'rgba(139, 21, 56, 0.12)', // Maroon-tinted divider
    action: {
      active: '#8B1538',
      hover: 'rgba(139, 21, 56, 0.08)',
      selected: 'rgba(139, 21, 56, 0.16)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(139, 21, 56, 0.12)',
    },
  },
  shape: {
    borderRadius: 8, // Keeping rounded corners
  },
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h6: {
      fontWeight: 600,
      fontSize: '1.15rem',
    },
    subtitle1: {
      fontSize: '0.875rem',
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.75rem',
    },
    button: {
      textTransform: 'none', // No uppercase buttons
      fontWeight: 500,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#6B0F2A', // Darker maroon header (same as Connect button)
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)', // White-tinted border
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        dense: {
          minHeight: 56,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#FFFFFF', // Pure white for cards/grid
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: '#8B1538', // Qatar maroon
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#6B0F2A', // Darker maroon on hover
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: 8,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: 'rgba(139, 159, 232, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(139, 159, 232, 0.24)',
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: '#4F6367', // Stormcloud icon color
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#4F6367',
          color: '#FFFFFF',
          fontSize: '0.875rem',
          fontWeight: 600,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          backgroundColor: '#FFFFFF', // Pure white for the grid
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        },
        columnHeaders: {
          backgroundColor: '#F8F8F8', // Light gray background for headers
          color: '#6B0F2A', // Maroon text on light background
        },
        row: {
          '&:hover': {
            backgroundColor: 'rgba(139, 21, 56, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(139, 21, 56, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(139, 21, 56, 0.18)',
            },
          },
        },
        cell: {
          borderBottom: '1px solid rgba(139, 21, 56, 0.1)',
        },
        footerContainer: {
          borderTop: '1px solid rgba(139, 21, 56, 0.12)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // Pure white
        },
        indicator: {
          backgroundColor: '#8B1538', // Qatar maroon indicator
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: 16,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& fieldset': {
            borderColor: 'rgba(139, 21, 56, 0.3)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(139, 21, 56, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#8B1538',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(139, 21, 56, 0.1)',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#F8F8F8', // Light gray for table headers
          color: '#6B0F2A', // Maroon text on light background
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#8B1538', // Qatar maroon checkbox
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontSize: '0.75rem',
        },
      },
    }
  },
});